import { useForm } from "@inertiajs/react";
import { Invoice, Merchant } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { format } from "date-fns";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/date-picker";
import Combobox from "@/components/combobox";

type Props = {
  invoice?: Invoice;
  merchants: Merchant[];
};

export function InvoiceForm({ invoice, merchants }: Props) {
  const { data, setData, post, patch, processing, errors, transform } = useForm(
    {
      merchant_id: invoice?.merchant?.id?.toString() ?? "",
      type: invoice?.type ?? "",
      status: invoice?.status ?? "",
      number: invoice?.number ?? "",
      issue_date: invoice?.issue_date ?? "",
      tax_event_date: invoice?.tax_event_date ?? "",
      due_date: invoice?.due_date ?? "",
      base_amount: invoice?.base_amount?.amount ?? "",
      vat_amount: invoice?.vat_amount?.amount ?? "",
      total_amount: invoice?.total_amount?.amount ?? "",
      currency: invoice?.currency ?? "",
    },
  );

  transform((data) => {
    return { invoice: data };
  });

  const invoiceTypes = [
    {
      label: "Expense",
      value: "expense",
    },
    {
      label: "Revenue",
      value: "revenue",
    },
  ];

  const invoiceStatuses = [
    {
      label: "Paid",
      value: "paid",
    },
    {
      label: "Pending",
      value: "pending",
    },
  ];

  function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();

    if (invoice) {
      patch(`/invoices/${invoice.id}`, {
        preserveScroll: "errors",
      });
    } else {
      post("/invoices", {
        preserveScroll: "errors",
      });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-3">
        <Label htmlFor="invoice_merchant_id">Merchant</Label>
        <Combobox
          itemType="merchant"
          id="invoice_merchant_id"
          value={data.merchant_id}
          items={merchants.map((merchant) => {
            return {
              label: merchant.name,
              value: merchant.id.toString(),
            };
          })}
          onSelect={(merchantId) => {
            setData("merchant_id", merchantId);
          }}
        />
        {errors.merchant_id && (
          <p className="text-destructive text-sm">{errors.merchant_id}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="invoice_type">Type</Label>
        <Combobox
          itemType="type"
          id="invoice_type"
          value={data.type}
          items={invoiceTypes}
          onSelect={(type) => {
            setData("type", type);
          }}
        />
        {errors.type && (
          <p className="text-destructive text-sm">{errors.type}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="invoice_status">Status</Label>
        <Combobox
          itemType="status"
          id="invoice_status"
          value={data.status}
          items={invoiceStatuses}
          onSelect={(status) => {
            setData("status", status);
          }}
        />
        {errors.status && (
          <p className="text-destructive text-sm">{errors.status}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="invoice_number">Number</Label>
        <Input
          id="invoice_number"
          value={data.number}
          onChange={(e) => setData("number", e.target.value)}
        />
        {errors.number && (
          <p className="text-destructive text-sm">{errors.number}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="invoice_date">Date</Label>
        <DatePicker
          id="invoice_date"
          selected={data.issue_date}
          onSelect={(date) => {
            if (date) {
              setData("issue_date", format(date, "yyyy-MM-dd"));
            } else {
              setData("issue_date", "");
            }
          }}
        />
        {errors.issue_date && (
          <p className="text-destructive text-sm">{errors.issue_date}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="tax_event_date">Tax event date</Label>
        <DatePicker
          id="tax_event_date"
          selected={data.tax_event_date}
          onSelect={(taxEventDate) => {
            if (taxEventDate) {
              setData("tax_event_date", format(taxEventDate, "yyyy-MM-dd"));
            } else {
              setData("tax_event_date", "");
            }
          }}
        />
        {errors.tax_event_date && (
          <p className="text-destructive text-sm">{errors.tax_event_date}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="invoice_date">Due date</Label>
        <DatePicker
          id="invoice_date"
          selected={data.due_date}
          onSelect={(dueDate) => {
            if (dueDate) {
              setData("due_date", format(dueDate, "yyyy-MM-dd"));
            } else {
              setData("due_date", "");
            }
          }}
        />
        {errors.due_date && (
          <p className="text-destructive text-sm">{errors.due_date}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="invoice_base_amount">Base amount</Label>
        <Input
          id="invoice_base_amount"
          type="number"
          step="0.01"
          value={data.base_amount}
          onChange={(e) => setData("base_amount", e.target.value)}
        />
        {errors.base_amount && (
          <p className="text-destructive text-sm">{errors.base_amount}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="invoice_vat_amount">VAT amount</Label>
        <Input
          id="invoice_vat_amount"
          type="number"
          step="0.01"
          value={data.vat_amount}
          onChange={(e) => setData("vat_amount", e.target.value)}
        />
        {errors.vat_amount && (
          <p className="text-destructive text-sm">{errors.vat_amount}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="invoice_total_amount">Total amount</Label>
        <Input
          id="invoice_total_amount"
          type="number"
          step="0.01"
          value={data.total_amount}
          onChange={(e) => setData("total_amount", e.target.value)}
        />
        {errors.total_amount && (
          <p className="text-destructive text-sm">{errors.total_amount}</p>
        )}
      </div>
      <div className="space-y-3">
        <Label htmlFor="invoice_currency">Currency</Label>
        <Input
          id="invoice_currency"
          value={data.currency}
          onChange={(e) => {
            setData("currency", e.target.value.toUpperCase());
          }}
        />
        {errors.currency && (
          <p className="text-destructive text-sm">{errors.currency}</p>
        )}
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={processing}>
          {invoice ? "Update invoice" : "Create invoice"}
        </Button>
      </div>
    </form>
  );
}
